import type { ExplorerRelation, ExplorerSchema } from '../api';
/** Ce qu'une action fait, dit en clair — le mot de Postgres n'apprend rien à qui ne l'a pas pratiqué. */
export const SENS_ACTION: Record<string, string> = {
  cascade: 'la suppression EMPORTE les lignes qui pointent ici',
  restrict: 'la suppression est REFUSÉE tant que des lignes pointent ici',
  noaction: 'la suppression échoue à la validation si des lignes pointent encore',
  setnull: 'les lignes qui pointent sont DÉTACHÉES (colonne vidée)',
  setdefault: 'les lignes qui pointent retombent sur leur valeur par défaut',
};

/**
 * La couleur d'un trait selon ce que la suppression fait.
 * ⚠️ TROIS TONS SEULEMENT, et ils viennent de la charte : ce qui EMPORTE (danger), ce qui EMPÊCHE
 * (attention), et le reste (neutre). Une couleur par action donnerait cinq teintes à mémoriser pour
 * une décision qui n'en a que trois.
 */
export function tonRelation(onDelete: string): { trait: string; libelle: string } {
  if (onDelete === 'cascade') return { trait: 'var(--da-fg)', libelle: 'cascade' };
  if (onDelete === 'restrict' || onDelete === 'noaction') return { trait: 'var(--wa-fg)', libelle: onDelete === 'restrict' ? 'restreint' : 'refuse' };
  // ⚠️ UNE RELATION DÉCLARÉE N'A PAS D'ACTION À LA SUPPRESSION, et il ne faut surtout pas lui en
  //    nommer une : rien n'est imposé, supprimer la cible laisse simplement une colonne qui pointe
  //    dans le vide. Écrire « refuse » ou « détache » ici inventerait un comportement de la base.
  if (onDelete === 'declared') return { trait: 'var(--ink400)', libelle: 'non imposée' };
  return { trait: 'var(--ink400)', libelle: onDelete === 'setnull' ? 'détache' : 'défaut' };
}

export interface Noeud { table: string; x: number; y: number; w: number; h: number; groupe: string }

export const LARGEUR = 208;
export const HAUT_TETE = 30;
const ECART_X = 248;
const ECART_Y = 12;
/** Au-delà, une colonne descend plus bas que n'importe quel écran et le dessin devient un couloir. */
const MAX_PAR_COLONNE = 14;

/**
 * PLACEMENT PAR DOMAINE — une colonne par groupe, qui SE REPLIE quand elle est trop haute.
 *
 * ⚠️ ON NE CHERCHE PAS À MINIMISER LES CROISEMENTS. Un placement « intelligent » déplacerait les
 * tables à chaque relecture, et on ne retrouverait jamais celle qu'on vient de regarder. Le
 * classement par domaine est STABLE et il a un sens pour le lecteur : le catalogue d'un côté, le
 * fiscal de l'autre. Un dessin qu'on relit vaut mieux qu'un dessin optimal.
 *
 * ⚠️ LE REPLI EXISTE PARCE QU'UN DOMAINE PEUT PORTER QUARANTE TABLES. Sans lui, une colonne mesure
 * deux mille pixels de haut : on ne la voit jamais en entier, et le premier geste est déjà de fuir.
 */
export function placer(schema: ExplorerSchema, maxParColonne = MAX_PAR_COLONNE): Noeud[] {
  const out: Noeud[] = [];
  let col = 0;
  for (const g of schema.groups) {
    let ligne = 0;
    for (const t of g.tables) {
      if (ligne >= maxParColonne) { col += 1; ligne = 0; }
      out.push({ table: t.name, groupe: g.g, x: col * ECART_X, y: ligne * (HAUT_TETE + ECART_Y), w: LARGEUR, h: HAUT_TETE });
      ligne += 1;
    }
    col += 1; // le domaine suivant commence à sa propre colonne
  }
  return out;
}

/** Les tables qu'une suppression dans `depart` emporterait, de proche en proche. */
export function emportees(depart: string, relations: readonly ExplorerRelation[]): Set<string> {
  const out = new Set<string>();
  // ⚠️ FILE ET NON RÉCURSION : une base a des cycles (une table qui se référence, deux tables qui se
  //    pointent), et une descente naïve y tournerait sans fin. L'ensemble déjà vu est la condition
  //    d'arrêt, pas une optimisation.
  const file = [depart];
  while (file.length) {
    const t = file.shift()!;
    for (const r of relations) {
      if (r.to !== t || r.onDelete !== 'cascade') continue;
      if (out.has(r.from) || r.from === depart) continue;
      out.add(r.from);
      file.push(r.from);
    }
  }
  return out;
}

/**
 * Le VOISINAGE d'une table — ce qu'elle référence et ce qui la référence, à `sauts` de distance.
 *
 * ⚠️ SANS DIRECTION, à la différence de la cascade : pour comprendre une table on veut ses parents
 * AUTANT que ses enfants. C'est cette liste qui rend un schéma de 180 tables lisible, en montrant la
 * dizaine qui compte au lieu du plan entier.
 */
export function voisinage(depart: string, relations: readonly ExplorerRelation[], sauts = 1): Set<string> {
  const vus = new Set<string>([depart]);
  let bord = [depart];
  for (let d = 0; d < sauts; d += 1) {
    const suivant: string[] = [];
    for (const t of bord) {
      for (const r of relations) {
        const autre = r.from === t ? r.to : r.to === t ? r.from : null;
        if (autre === null || vus.has(autre)) continue;
        vus.add(autre);
        suivant.push(autre);
      }
    }
    bord = suivant;
  }
  return vus;
}

// ── LA VUE : zoom et déplacement ──────────────────────────────────────────────────────────────────

export interface Vue { k: number; tx: number; ty: number }
export const ZOOM_MIN = 0.12;
export const ZOOM_MAX = 2.6;

/** Un facteur de zoom hors bornes ne se refuse pas, il se RAMÈNE — sinon la molette semble cassée. */
export function bornerZoom(k: number): number {
  if (!Number.isFinite(k)) return 1;
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, k));
}

/**
 * Zoomer AUTOUR D'UN POINT — le point visé ne bouge pas d'un pixel.
 * ⚠️ C'est la seule façon dont une molette paraît juste : zoomer autour du coin de l'écran donne
 * l'impression que le dessin s'enfuit, et on passe son temps à le rattraper.
 */
export function zoomAutour(vue: Vue, facteur: number, point: { x: number; y: number }): Vue {
  const k = bornerZoom(vue.k * facteur);
  const reel = k / vue.k; // le facteur VRAIMENT appliqué une fois les bornes respectées
  return { k, tx: point.x - (point.x - vue.tx) * reel, ty: point.y - (point.y - vue.ty) * reel };
}

/** Un point de l'écran ramené aux coordonnées du dessin (pour savoir ce qu'on a saisi). */
export function versGraphe(point: { x: number; y: number }, vue: Vue): { x: number; y: number } {
  return { x: (point.x - vue.tx) / vue.k, y: (point.y - vue.ty) / vue.k };
}

export interface Cadre { x: number; y: number; w: number; h: number }

/** Le rectangle qui contient tout le dessin — vide si rien n'est placé. */
export function cadreDe(noeuds: readonly Noeud[]): Cadre | null {
  if (noeuds.length === 0) return null;
  const x1 = Math.min(...noeuds.map((n) => n.x));
  const y1 = Math.min(...noeuds.map((n) => n.y));
  const x2 = Math.max(...noeuds.map((n) => n.x + n.w));
  const y2 = Math.max(...noeuds.map((n) => n.y + n.h));
  return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
}

/**
 * La vue qui fait tenir `cadre` dans `ecran`, centrée.
 * ⚠️ ELLE EST APPLIQUÉE AU CHARGEMENT, et c'est ce qui répond au « l'écran est immense » : un dessin
 * qu'on découvre à l'échelle 1 commence toujours dans un coin, sur une table prise au hasard.
 */
export function ajusterVue(cadre: Cadre | null, ecran: { w: number; h: number }, marge = 40): Vue {
  if (!cadre || cadre.w <= 0 || cadre.h <= 0 || ecran.w <= 0 || ecran.h <= 0) return { k: 1, tx: marge, ty: marge };
  // Le cadrage automatique reste à une taille de lecture ; le zoom manuel peut aller plus loin.
  const k = bornerZoom(Math.min(1.25, (ecran.w - marge * 2) / cadre.w, (ecran.h - marge * 2) / cadre.h));
  return { k, tx: (ecran.w - cadre.w * k) / 2 - cadre.x * k, ty: (ecran.h - cadre.h * k) / 2 - cadre.y * k };
}

/**
 * Les deux bouts d'un trait, pris sur les FLANCS qui se font face.
 * ⚠️ SANS ÇA, UN TRAIT PART TOUJOURS À DROITE : quand la cible est à gauche, il contourne sa propre
 * tuile et le dessin devient un plat de spaghettis. On choisit le flanc selon la position réelle.
 */
export function ancrages(a: Noeud, b: Noeud): { x1: number; y1: number; x2: number; y2: number; c: number; c2?: number } {
  // Deux tables empilées se relient par le même flanc, sans traverser leurs intitulés.
  if (Math.abs(a.x - b.x) < 10) return { x1: a.x + a.w, y1: a.y + HAUT_TETE / 2, x2: b.x + b.w, y2: b.y + HAUT_TETE / 2, c: 60, c2: 60 };
  const versLaDroite = b.x + b.w / 2 >= a.x + a.w / 2;
  const x1 = versLaDroite ? a.x + a.w : a.x;
  const x2 = versLaDroite ? b.x : b.x + b.w;
  const ecart = Math.max(30, Math.abs(x2 - x1) / 2);
  return { x1, y1: a.y + HAUT_TETE / 2, x2, y2: b.y + HAUT_TETE / 2, c: versLaDroite ? ecart : -ecart };
}

/** Où la disposition d'une base est retenue — une par base, sinon deux magasins se marchent dessus. */
export function clePositions(baseId: string): string {
  return `oz-explorer-graphe:${baseId}`;
}
